/**
 * Client-side API wrapper for the push-prompt endpoints. Kept pure
 * (no React, no imports from components) so it's trivially testable
 * and reusable from the modal + from a future "notifications" tab
 * that shows the same state.
 */
import { API_BASE } from "./api";

// Client-side mirror of `src/lib/push-prompt-decision.ts`'s Decision
// type. Not imported directly because web/ has its own tsconfig +
// bundler; pulling from the server-side module would drag in Prisma
// types the browser bundle mustn't ship. Keep this in sync manually.
export type DecisionReason =
  | "already_granted_and_registered"
  | "never_asked"
  | "granted_but_no_token"
  | "system_denied_permanent"
  | "cooloff_active"
  | "cooloff_elapsed_reprompt"
  | "max_prompts_reached"
  | "web_install_recently_dismissed"
  | "web_install_cooloff_elapsed_reshow"
  | "unknown_response_no_reprompt";
export type Decision = {
  show: boolean;
  reason: DecisionReason;
  variant: "native_permission" | "web_install";
};

export async function fetchPushPromptDecision(input: {
  venueId: string;
  isNativeApp: boolean;
}): Promise<Decision | null> {
  // Build the query via URLSearchParams and concatenate, rather than new URL().
  // API_BASE is "" in the SPA (same-origin), and `new URL("/api/...")` with no
  // base throws TypeError("Invalid URL") synchronously, which propagates as
  // an unhandled promise rejection because the one caller only chains .then().
  // Observed in prod on 2026-10-09 (Kyle PESO console). Fail-silent catch
  // below only wraps the fetch, not the constructor.
  const qs = new URLSearchParams({
    venueId: input.venueId,
    isNativeApp: input.isNativeApp ? "true" : "false",
  });
  try {
    const res = await fetch(`${API_BASE}/api/me/push-prompt?${qs.toString()}`, {
      credentials: "include",
    });
    if (res.status === 401) return null; // not signed in
    if (!res.ok) return null;
    return (await res.json()) as Decision;
  } catch {
    // Network errors etc: fail silent, the modal simply doesn't
    // appear this session. Better than crashing the page.
    return null;
  }
}

export type PromptResponse =
  | "granted"
  | "system_denied"
  | "soft_dismissed"
  | "web_install_dismissed";

export async function recordPushPromptResponse(input: {
  venueId: string;
  response: PromptResponse;
}): Promise<{ ok: true; promptCount: number } | null> {
  try {
    const res = await fetch(`${API_BASE}/api/me/push-prompt`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!res.ok) return null;
    return (await res.json()) as { ok: true; promptCount: number };
  } catch {
    return null;
  }
}
